import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { X } from "lucide-react"
import { mitGesetztemWert } from "@/lib/auswahlOptionen"
import { useVerschiebbar } from "@/hooks/useVerschiebbar"
import { wegSchritt } from "@/lib/wege"
import { darfAufgabeAnlegen } from "@/lib/aufgabeVerknuepfung"

/**
 * Interne Aufgabe schnell anlegen — von überall aus (#33).
 *
 * Gewünscht am 25.08.2026: „Können wir außerdem auf jeder seite neben der
 * suchlupe ein symbol für interne aufgabe erstellen hinzufügen? Da kommt dann
 * ein modal für eine interne aufgabe."
 *
 * 🔴 `createPortal` ist Pflicht: das Modal hängt sonst im Layout-Kontext der
 * jeweiligen Seite und wird von deren `overflow`/`transform` beschnitten. Beim
 * Kontakt-Modal ist genau das schon passiert.
 */

export function InterneAufgabeModal({
  open, onClose, tenantSlug, emailId, callId, heroProjectId, titelVorschlag, notizVorschlag, zustaendigVorschlag,
}: {
  open: boolean
  onClose: () => void
  /** Vorbelegung, wenn die Seite einen Mandanten im Kontext hat. Ohne sie
   *  waehlt der Nutzer selbst — bis 26.08.2026 gab es dafuer kein Feld, und
   *  jede Aufgabe aus dem Schnellzugriff landete ohne Mandanten (#39). */
  tenantSlug?: string | null
  /** Woraus die Aufgabe entsteht (#39). Wird mitgespeichert und in der Liste
   *  als Rückweg zur Quelle angeboten. */
  emailId?: number | null
  callId?: number | null
  heroProjectId?: number | null
  /** Vorbelegter Titel, wenn der Aufrufer schon weiß, worum es geht. */
  titelVorschlag?: string | null
  /** Vorbelegte Notiz, z. B. die Voice-Call-Spur eines KI-Telefon-Anrufs. */
  notizVorschlag?: string | null
  /** Vorbelegte Zuständigkeit, z. B. wem der Anruf schon zugeteilt ist. */
  zustaendigVorschlag?: string | null
}) {
  const qc = useQueryClient()
  const [titel, setTitel] = useState("")
  const [notiz, setNotiz] = useState("")
  const [faellig, setFaellig] = useState("")
  const [zustaendig, setZustaendig] = useState("")
  const [tenant, setTenant] = useState("")
  const [prioritaet, setPrioritaet] = useState("normal")
  const [fehler, setFehler] = useState<string | null>(null)
  const { griff, stil } = useVerschiebbar(open)

  const zustaendige = useQuery<{ items: string[] }>({
    queryKey: ["portal-aufgaben-zustaendige"],
    queryFn: async () => {
      const r = await fetch("/api/v1/portal-aufgaben/zustaendige", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: open,
    staleTime: 1000 * 60 * 10,
  })

  const mandanten = useQuery<{ items: { slug: string; name: string }[] }>({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => {
      const r = await fetch("/api/v1/tenants", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: open,
    staleTime: 1000 * 60 * 5,
  })

  // Beim Öffnen zurücksetzen — sonst steht beim zweiten Mal noch die vorige
  // Aufgabe im Feld.
  useEffect(() => {
    if (!open) return
    setTitel(titelVorschlag || ""); setNotiz(notizVorschlag || ""); setFaellig("")
    setZustaendig(zustaendigVorschlag || ""); setPrioritaet("normal"); setFehler(null)
    // Der Mandant der Seite ist der Vorschlag, nicht die Vorschrift — er steht
    // im Feld und laesst sich umstellen.
    setTenant(tenantSlug || "")
  }, [open, tenantSlug, titelVorschlag, notizVorschlag, zustaendigVorschlag])

  useEffect(() => {
    if (!open) return
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", esc)
    return () => window.removeEventListener("keydown", esc)
  }, [open, onClose])

  const anlegen = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/v1/portal-aufgaben", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          titel, notiz: notiz || null, faellig_am: faellig || null,
          zustaendig: zustaendig || null, tenant_slug: tenant || null,
          prioritaet, email_id: emailId ?? null, call_id: callId ?? null,
          hero_project_id: heroProjectId ?? null,
        }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      return d
    },
    onSuccess: () => {
      wegSchritt("interne_aufgabe_angelegt")
      qc.invalidateQueries({ queryKey: ["portal-aufgaben"] })
      onClose()
    },
    onError: (e) => setFehler(e instanceof Error ? e.message : String(e)),
  })

  if (!open) return null

  const feld = {
    width: "100%", fontSize: 13, padding: "8px 10px", borderRadius: 7,
    border: "1px solid var(--border-color)", background: "var(--bg-card)",
    color: "var(--text)", outline: "none",
  } as const
  const beschriftung = {
    display: "block", fontSize: 11.5, fontWeight: 600, letterSpacing: 0.3,
    textTransform: "uppercase" as const, color: "var(--text-muted)", marginBottom: 4,
  }

  return createPortal(
    <div
      className="pp-overlay oben"
      data-kontext-popup="Interne Aufgabe anlegen"
      // 🔴 `onMouseDown` mit `target === currentTarget`, NICHT `onClick`.
      // Seit die Dialoge verschiebbar sind (#47), endet ein Zug aus dem Kasten
      // heraus mit dem Loslassen ueber der Huelle — und ein `onClick` dort
      // schloss den Dialog mitten im Verschieben. Beim Durchklicken auf dev
      // gefunden (26.08.2026); die Hook-Tests konnten es nicht sehen, sie
      // kennen die Huelle nicht.
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(15,23,42,0.35)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        paddingTop: "12vh",
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 460, maxWidth: "calc(100vw - 32px)",
          background: "var(--bg-card)", borderRadius: 12,
          border: "1px solid var(--border-color)",
          boxShadow: "0 18px 50px rgba(15,23,42,0.22)",
          overflow: "hidden",
          ...stil,
        }}
      >
        {/* Die Kopfzeile ist der Griff (#47) — Knöpfe darin bleiben Knöpfe. */}
        <div {...griff} style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "13px 16px", borderBottom: "1px solid var(--border-color)",
          ...(griff.style || {}),
        }}>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 600 }}>Interne Aufgabe</div>
            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 1 }}>
              Bleibt im Portal — geht nicht nach HERO
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Schließen"
                  style={{ background: "none", border: "none", cursor: "pointer",
                           color: "var(--text-muted)", padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        <form
          onSubmit={async (e) => {
            e.preventDefault()
            if (!titel.trim()) return
            // #149: interne Aufgaben haben keinen Vorgang als Pflicht — hier
            // zaehlt nur, ob jemand zustaendig ist. Hinweis, kein Verbot.
            if (!(await darfAufgabeAnlegen({ mitarbeiter: zustaendig, ohneProjektPruefung: true }))) return
            anlegen.mutate()
          }}
          style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}
        >
          <div>
            <label style={beschriftung}>Was ist zu tun?</label>
            <input autoFocus value={titel} onChange={(e) => setTitel(e.target.value)}
                   placeholder="Kurz und konkret" style={feld} />
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={beschriftung}>Zuständig</label>
              <select aria-label="Zuständig" value={zustaendig}
                      onChange={(e) => setZustaendig(e.target.value)} style={feld}>
                <option value="">— niemand —</option>
                {mitGesetztemWert(zustaendige.data?.items ?? [], zustaendig).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={beschriftung}>Fällig</label>
              <input type="date" value={faellig} onChange={(e) => setFaellig(e.target.value)} style={feld} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={beschriftung}>Priorität</label>
              <select aria-label="Priorität" value={prioritaet}
                      onChange={(e) => setPrioritaet(e.target.value)} style={feld}>
                <option value="hoch">Hoch</option>
                <option value="normal">Normal</option>
                <option value="niedrig">Niedrig</option>
              </select>
            </div>
          </div>

          <div>
            <label style={beschriftung}>Mandant</label>
            {/* 🔑 Der vorbelegte Wert MUSS als eigene Option dabeisein, auch
                wenn die Liste noch laedt oder ihn nicht kennt — sonst zeigt
                das Feld „— keiner —", obwohl ein Mandant gesetzt ist, und das
                ist von „wirklich keiner" nicht zu unterscheiden. Dieselbe
                Falle wie beim Kalender-Dialog. */}
            <select aria-label="Mandant" value={tenant}
                    onChange={(e) => setTenant(e.target.value)} style={feld}>
              <option value="">— keiner (nur fürs Büro) —</option>
              {tenant && !(mandanten.data?.items ?? []).some((m) => m.slug === tenant) && (
                <option value={tenant}>{tenant}</option>
              )}
              {(mandanten.data?.items ?? []).map((m) => (
                <option key={m.slug} value={m.slug}>{m.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={beschriftung}>Notiz</label>
            <textarea rows={3} value={notiz} onChange={(e) => setNotiz(e.target.value)}
                      placeholder="Optional" style={{ ...feld, resize: "vertical" }} />
          </div>

          {fehler && (
            <div style={{ fontSize: 12, color: "var(--destructive)" }}>{fehler}</div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" onClick={onClose}
                    style={{ fontSize: 13, padding: "7px 13px", borderRadius: 7,
                             border: "1px solid var(--border-color)",
                             background: "transparent", color: "var(--text-muted)",
                             cursor: "pointer" }}>
              Abbrechen
            </button>
            <button type="submit" disabled={!titel.trim() || anlegen.isPending}
                    style={{ fontSize: 13, fontWeight: 500, padding: "7px 15px",
                             borderRadius: 7, border: "none",
                             background: titel.trim() ? "#3C3489" : "rgba(44,44,42,0.12)",
                             color: titel.trim() ? "#fff" : "var(--text-muted)",
                             cursor: titel.trim() ? "pointer" : "not-allowed" }}>
              {anlegen.isPending ? "Legt an …" : "Anlegen"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  )
}
